import { z } from 'zod'

export const AbsoluteURLZ = z.string().brand<'AbsoluteURL'>()

export type AbsoluteURL = z.infer<typeof AbsoluteURLZ>

// eslint-disable-next-line no-restricted-syntax
export enum UserCurrency {
	AUD = 'AUD',
	EUR = 'EUR',
	GBP = 'GBP',
	ILS = 'ILS',
	SEK = 'SEK',
	USD = 'USD',
}
export const UserCurrencyZ = z.enum(UserCurrency)

export const CurrencyZ = z.enum([
	'BGN',
	'BRL',
	'CAD',
	'CHF',
	'CNY',
	'CZK',
	'DKK',
	'HKD',
	'HUF',
	'IDR',
	'INR',
	'ISK',
	'JPY',
	'KRW',
	'MXN',
	'MYR',
	'NOK',
	'NZD',
	'PHP',
	'PLN',
	'RON',
	'SGD',
	'THB',
	'TRY',
	'ZAR',
	...Object.values(UserCurrency),
])
export type Currency = z.infer<typeof CurrencyZ>
export const currency = CurrencyZ.enum

export const CurrencyObjectZ = z.object({
	/** @example EUR */
	currency_code: UserCurrencyZ,
	/** @example 5.2 */
	amount: z.string(),
})

export type CurrencyObject = z.infer<typeof CurrencyObjectZ>

export const CurrencyNumberObjectZ = z.object({
	/** @example EUR */
	currency_code: UserCurrencyZ,
	/** @example 5.2 */
	amount: z.number(),
})

export type CurrencyNumberObject = z.infer<typeof CurrencyNumberObjectZ>

export const EmailZ = z.email()

export type Email = z.infer<typeof EmailZ>

// eslint-disable-next-line no-restricted-syntax
export enum Gender {
	Female = 'female',
	Male = 'male',
}
export const GenderZ = z.enum(Gender)

export const ISOCountry2Z = z.string().brand<'ISOCountry2'>()
export type ISOCountry2 = z.infer<typeof ISOCountry2Z>

export const ISODateStringZ = z.string().datetime({ offset: true })
export type ISODateString = z.infer<typeof ISODateStringZ>

export const OriginalAmountZ = z.strictObject({
	amount: z.string(),
	currency: UserCurrencyZ,
	billing_date: ISODateStringZ.nullable(),
})
export type OriginalAmount = z.infer<typeof OriginalAmountZ>

export const StatsBucketsZ = z.object({
	'0': z.number().nullable(),
	'25': z.number().nullable(),
	'50': z.number().nullable(),
	'75': z.number().nullable(),
	'100': z.number().nullable(),
})
export type StatsBuckets = z.infer<typeof StatsBucketsZ>

export const StatsBucketsPerItemZ = z.record(z.string(), StatsBucketsZ)
export type StatsBucketsPerItem = z.infer<typeof StatsBucketsPerItemZ>

export const PricesMetadataZ = z
	.object({ total: StatsBucketsZ })
	.and(z.record(z.number().or(z.string()), StatsBucketsZ))
export type PricesMetadata = z.infer<typeof PricesMetadataZ>

export const RelativeURLZ = z.string().brand<'RelativeURL'>()
export type RelativeURL = z.infer<typeof RelativeURLZ>

export const ShortDistanceUnitsZ = z.enum(['KM', 'MI'])
export type ShortDistanceUnits = z.infer<typeof ShortDistanceUnitsZ>

export const TimezoneZ = z.string().brand<'Timezone'>()
export type Timezone = z.infer<typeof TimezoneZ>

export const CountryNameZ = z.string()
export type CountryName = z.infer<typeof CountryNameZ>

export const POSCountryNameZ = z.string()

// eslint-disable-next-line no-restricted-syntax
export enum CancellationStatus {
	NeedToBeCancelled = 'need_to_be_cancelled',
	WaitingCancelConfirmation = 'waiting_cancel_confirmation',
	Cancelled = 'canceled',
	CancellationFailed = 'cancellation_failed',
	Changed = 'changed',
}

export interface WithCancellationStatus {
	cancellation_status: CancellationStatus | null
}

export type ComparisonFn<T> = (a: T, b: T) => boolean

/** @deprecated Use Comparator<T> */
export type SortingFn<T> = (a: T, b: T) => number

type RequireExcept<T, K extends keyof T> = Omit<Required<T>, K> & Pick<T, K>
export type PartialRequireExcept<T, K extends keyof T> = Partial<RequireExcept<T, K>>

/** Either `T` or `Array<T>` */
export type ArrayOr<T> = T | T[]

export interface WithGenericId<T> {
	id: T
}

export type Identifiable = WithGenericId<number>

export interface WithName {
	name: string
}

/** Used on search, corresponds to BookingSegmentProductType on the backend */
// eslint-disable-next-line no-restricted-syntax
export enum ProductType {
	Flights = 'flights',
	Hotels = 'hotels',
	Cars = 'cars',
	Rails = 'rails',
}
export const ProductTypeZ = z.enum(ProductType)

export const TripIdZ = z.number() // .brand<'TripId'>()
export type TripId = z.infer<typeof TripIdZ>

// eslint-disable-next-line no-restricted-syntax
export enum LocationTypes {
	Home = 'home',
	Office = 'office',
	Client = 'client',
	Hotel = 'hotel',
	Airport = 'airport',
	Other = 'other',
	City = 'city',
	Country = 'country',
	CarRentAgency = 'car_rent_agency',
	TrainStation = 'train',
	Rail = 'rail',
	AllStation = 'rail_group',
}
export const LocationTypesZ = z.enum(LocationTypes)

// TODO: Fix Expense forms to be able to use brands
export const UserIdZ = z.number() // .brand<'UserId'>()
export type UserId = z.infer<typeof UserIdZ>

export interface WithUserId<T = UserId> {
	user_id: T
}

export interface LoyaltyProgramCardBase extends WithUserId {
	id: number
}

export interface WithLoyaltyProgramName {
	loyalty_program_name: string
}

export interface CountryType {
	code: string
	name?: string | null
}

export interface SegmentToTraveler extends WithUserId {
	booking_segment_id: number
}

// eslint-disable-next-line no-restricted-syntax
export enum TimeFrame {
	Past = 'past',
	Current = 'current',
	All = 'all',
}
